import React from 'react';
import { Input, Tag, Space, Typography } from 'antd';

// ChipEditor — blur/Enter-commit tag list for an array<string> policy field (MCP
// allow/deny lists, network egress targets, regex-pattern lists, …). The David-SE-console
// chip UX, re-expressed on corenet/antd primitives. Reusable policy-form primitive: the
// schema-driven authoring editor renders one per array<string|enum> stanza.
//
// - Type + Enter (or blur) commits the trimmed value; duplicates are ignored.
// - Each value is a closable Tag; close removes it.
// - `suggestions` (e.g. an enum's allowed values) render as clickable add-chips.

export interface ChipEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Allowed/known values offered as one-click adds (e.g. an enum). */
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
  'aria-label'?: string;
  /** Optional syntax validator. Return an error message to reject the entry (chip is not
   *  added, message shown inline), or null/undefined to accept. Omit for no validation
   *  (existing behavior, unchanged). */
  validate?: (raw: string) => string | null | undefined;
}

export const ChipEditor: React.FC<ChipEditorProps> = ({
  value,
  onChange,
  suggestions,
  placeholder = 'Add a value, press Enter',
  disabled,
  'aria-label': ariaLabel,
  validate,
}) => {
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const add = (raw: string) => {
    const v = raw.trim();
    if (!v) {
      setDraft('');
      setError(null);
      return;
    }
    if (validate) {
      const err = validate(v);
      if (err) {
        setError(err);
        return; // keep draft so the author can fix it in place
      }
    }
    setDraft('');
    setError(null);
    if (value.includes(v)) return;
    onChange([...value, v]);
  };

  const remove = (v: string) => onChange(value.filter((x) => x !== v));

  const unusedSuggestions = (suggestions ?? []).filter((s) => !value.includes(s));

  return (
    <div aria-label={ariaLabel}>
      {value.length > 0 && (
        <Space wrap size={[4, 4]} style={{ marginBottom: 8 }}>
          {value.map((v) => (
            <Tag
              key={v}
              closable={!disabled}
              onClose={(e) => {
                e.preventDefault();
                remove(v);
              }}
            >
              {v}
            </Tag>
          ))}
        </Space>
      )}
      <Input
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        status={error ? 'error' : undefined}
        aria-label={ariaLabel ? `${ariaLabel} input` : undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onPressEnter={() => add(draft)}
        onBlur={() => add(draft)}
      />
      {error && (
        <Typography.Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {error}
        </Typography.Text>
      )}
      {unusedSuggestions.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>
            Suggested:
          </Typography.Text>
          <Space wrap size={[4, 4]}>
            {unusedSuggestions.map((s) => (
              <Tag
                key={s}
                style={{ cursor: disabled ? 'default' : 'pointer', borderStyle: 'dashed' }}
                onClick={() => !disabled && add(s)}
              >
                + {s}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};

export default ChipEditor;
